<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';
import { dateShort, dateTile, occurrenceLabel, timeRange } from './eventTime.js';
import { useEventSignup } from '../useEventSignup.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';
import WaiverDocument from '@/Components/WaiverDocument.vue';
import TicketPicker from '../TicketPicker.vue';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Everything the form DOES is in ../useEventSignup.js, which Classic's
// EventSignupBlock reads too. This file is the design: the mockup's
// event-detail aside, which is why it is a narrow white card rather
// than a full-width panel.
const signup = useEventSignup(() => props.config);
const {
    event,
    signupCfg,
    isRecurring, occurrences, selectedDate,
    waitlistTaken, capacity, remaining, isFull, closedReason,
    name, email, phone, partySize, notes, waiverAgreed, submitting,
    waiverEnabled, waiverText, waiverFile,
    turnstileSiteKey, turnstileContainer,
    flashMsg, flashLevel, submitErrors,
    submit, icsUrl,
    eyebrow, heading, subtitle, buttonLabel, fields,
    tickets, ticketsCount, ticketsTooMany, soldOut,
} = signup;

const editing = inject('tiqEditorCanvas', false);

const tile = computed(() => dateTile(event));
const whenLine = computed(() => [dateShort(event), timeRange(event)].filter(Boolean).join(', '));
const whereLine = computed(() => event?.location || '');

// The design's one mono line between two rules. It says how many
// places are left, or that the list has turned into a waitlist, and
// says nothing at all on an event with no capacity set.
const spotsLine = computed(() => {
    if (!capacity.value) return '';
    if (isFull.value) {
        if (tickets.value) return 'Sold out';
        return waitlistTaken.value > 0
            ? `Full · ${waitlistTaken.value} on the waitlist`
            : 'Full · waitlist open';
    }
    return `${remaining.value} of ${capacity.value} ${tickets.value ? 'tickets' : 'places'} left`;
});
</script>

<template>
    <section v-if="event ? signupCfg.enabled : editing"
             :id="config.anchor_id || null"
             class="th-block th-sec th-esignup"
             :style="styleVars(config)">
        <div class="th-wrap th-esignup__wrap">
            <aside class="th-card th-esignup__card">
                <header v-if="eyebrow || heading || subtitle" class="th-esignup__head">
                    <p v-if="eyebrow" class="th-eyebrow">{{ eyebrow }}</p>
                    <h2 v-if="heading" class="th-h2 th-h2--sm">{{ heading }}</h2>
                    <p v-if="subtitle" class="th-small th-esignup__sub">{{ subtitle }}</p>
                </header>

                <p v-if="!event" class="th-esignup__empty">
                    The RSVP form appears here when the template renders a real event.
                </p>

                <template v-else>
                    <div v-if="flashMsg" class="th-form-note"
                         :class="flashLevel === 'error' ? 'th-form-note--error' : 'th-form-note--ok'">
                        {{ flashMsg }}
                    </div>

                    <p v-if="closedReason" class="th-esignup__closed">{{ closedReason }}</p>
                    <p v-else-if="soldOut" id="tiq-es-sold-out" class="th-esignup__closed">Sold out.</p>

                    <template v-else>
                        <div class="th-esignup__when">
                            <div class="th-date">
                                <span class="th-date__m">{{ tile.month }}</span>
                                <span class="th-date__d">{{ tile.day }}</span>
                            </div>
                            <div class="th-esignup__whentext">
                                <p v-if="whenLine" class="th-esignup__whenline">{{ whenLine }}</p>
                                <p v-if="whereLine" class="th-esignup__whereline">{{ whereLine }}</p>
                            </div>
                        </div>

                        <p v-if="spotsLine" class="th-mono th-esignup__spots">{{ spotsLine }}</p>
                        <p v-if="isFull && !tickets" class="th-small th-esignup__waitnote">
                            Sign up below to join the waitlist. We will be in touch if a place opens up.
                        </p>

                        <form class="th-esignup__form" @submit.prevent="submit">
                            <label v-if="isRecurring && occurrences.length" class="th-label">
                                Which date
                                <select v-model="selectedDate" class="th-input th-esignup__select" required>
                                    <option v-for="d in occurrences" :key="d" :value="d">{{ occurrenceLabel(d) }}</option>
                                </select>
                            </label>

                            <label class="th-label">
                                {{ fields.name_required ? 'Name' : 'Name (optional)' }}
                                <input v-model="name" class="th-input th-esignup__name" type="text"
                                       :required="fields.name_required" maxlength="120">
                                <span v-if="submitErrors.name" class="th-field-error">{{ submitErrors.name }}</span>
                            </label>

                            <label v-if="fields.email_visible" class="th-label">
                                {{ fields.email_required ? 'Email' : 'Email (optional)' }}
                                <input v-model="email" class="th-input th-esignup__email" type="email"
                                       :required="fields.email_required" maxlength="200">
                                <EmailTypoHint :email="email" @fix="email = $event" />
                                <span v-if="submitErrors.email" class="th-field-error">{{ submitErrors.email }}</span>
                            </label>

                            <label v-if="fields.phone_visible" class="th-label">
                                {{ fields.phone_required ? 'Phone' : 'Phone (optional)' }}
                                <input v-model="phone" class="th-input th-esignup__phone" type="tel"
                                       :required="fields.phone_required" maxlength="32">
                                <span v-if="submitErrors.phone" class="th-field-error">{{ submitErrors.phone }}</span>
                            </label>

                            <label v-if="fields.party_size_visible && !tickets" class="th-label th-esignup__party">
                                How many of you
                                <input v-model="partySize" class="th-input" type="number" min="1" max="50">
                            </label>

                            <label v-if="fields.notes_visible" class="th-label">
                                {{ fields.notes_required ? 'Anything we should know?' : 'Anything we should know? (optional)' }}
                                <textarea v-model="notes" class="th-input th-esignup__notes" rows="3"
                                          :required="fields.notes_required" maxlength="1000"></textarea>
                                <span v-if="submitErrors.notes" class="th-field-error">{{ submitErrors.notes }}</span>
                            </label>

                            <TicketPicker v-if="tickets" :signup="signup" :accent="accent || undefined" />

                            <div v-if="turnstileSiteKey" ref="turnstileContainer" class="th-esignup__turnstile"></div>

                            <div v-if="waiverEnabled" class="th-esignup__waiver">
                                <p class="th-mono th-mono--caps th-esignup__waiverlabel">Liability waiver</p>
                                <WaiverDocument v-if="waiverFile" :file="waiverFile" />
                                <div v-else class="th-esignup__waivertext">{{ waiverText }}</div>
                                <label class="th-check th-esignup__waiveragree">
                                    <input v-model="waiverAgreed" type="checkbox" required>
                                    <span>I have read and agree to this waiver.</span>
                                </label>
                                <span v-if="submitErrors.waiver_agreed" class="th-field-error">{{ submitErrors.waiver_agreed }}</span>
                            </div>

                            <button type="submit" id="tiq-es-submit"
                                    class="th-btn th-btn--primary th-btn--block th-esignup__submit"
                                    :disabled="submitting || editing || (waiverEnabled && !waiverAgreed) || (tickets && (ticketsCount === 0 || ticketsTooMany))">
                                {{ submitting ? (tickets ? 'Opening payment' : 'Sending') : (isFull ? 'Join the waitlist' : buttonLabel) }}
                            </button>
                            <p v-if="editing" class="th-small th-esignup__previewnote">
                                Preview only. Visitors can RSVP from the published page.
                            </p>
                        </form>

                        <a v-if="icsUrl" class="th-btn th-btn--quiet th-btn--block th-esignup__ics" :href="icsUrl">
                            Add to calendar
                        </a>
                    </template>
                </template>
            </aside>
        </div>
    </section>
</template>

<style scoped>
.th-esignup__wrap { max-width: 1000px; }
/* The mockup's aside is a column of the event-detail layout. Blocks
   are stacked siblings here, so the card keeps the aside's width and
   sits where the reader has been reading rather than being stretched
   across the page. */
.th-esignup__card {
    max-width: 420px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.th-esignup__head { display: flex; flex-direction: column; gap: 8px; }
.th-esignup__head .th-eyebrow { margin-bottom: 0; }
.th-esignup__sub { color: var(--text-sub, var(--th-muted)); }
.th-esignup__when { display: flex; gap: 16px; align-items: center; }
.th-esignup__whentext { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.th-esignup__whenline { margin: 0; font-weight: 600; }
.th-esignup__whereline { margin: 0; font-size: 15px; color: var(--th-muted); }
.th-esignup__spots {
    margin: 0;
    padding: 12px 0;
    border-top: 1px solid var(--th-line-soft);
    border-bottom: 1px solid var(--th-line-soft);
    letter-spacing: .06em;
    text-transform: uppercase;
    font-weight: 500;
}
.th-esignup__waitnote { margin: 0; }
.th-esignup__form { display: flex; flex-direction: column; gap: 14px; }
.th-esignup__party { max-width: 140px; }
.th-esignup__select { padding-right: 10px; }
.th-esignup__waiver {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid var(--th-line);
    border-radius: var(--r);
    background: var(--tint);
}
.th-esignup__waiverlabel { margin: 0; }
.th-esignup__waivertext {
    max-height: 180px;
    overflow-y: auto;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid var(--th-line);
    border-radius: var(--r);
    font-size: 14px;
    line-height: 1.55;
    white-space: pre-wrap;
}
.th-esignup__waiveragree { align-items: flex-start; border-radius: var(--r); text-align: left; }
.th-esignup__previewnote { margin: 0; color: var(--th-faint); }
.th-esignup__closed {
    margin: 0;
    padding: 18px;
    text-align: center;
    font-size: 15px;
    color: var(--th-muted);
    background: var(--tint);
    border-radius: var(--r);
}
.th-esignup__empty {
    margin: 0;
    padding: 28px;
    text-align: center;
    font-size: 15px;
    color: var(--th-faint);
    border: 1px dashed var(--th-field);
    border-radius: var(--r);
    background: rgba(0, 0, 0, .02);
}
</style>
